/**
 * src/components/Financial/HoroscopeChart.jsx
 * 【完全版：isHeliocentric定義エラー修正 + 地球表示対応】
 */

import React, {
  useRef,
  useMemo,
  useCallback,
  useEffect,
  useState,
} from "react";
import * as Astro from "../../astronomy.js";
import {
  PLANET_SYMBOLS,
  PLANETS,
  PLANET_COLORS,
} from "../../utils/constants.js";
import { Download } from "lucide-react";

const EXTENDED_POINTS = ["NorthNode", "SouthNode", "ASC", "DSC", "MC", "IC"];

const ASPECT_DEFS = [
  {
    name: "Conjunction",
    symbol: "☌",
    angle: 0,
    orb: 8,
    color: "#333333",
    type: "Hard",
    category: "major",
  },
  {
    name: "Opposition",
    symbol: "☍",
    angle: 180,
    orb: 8,
    color: "#e74c3c",
    type: "Hard",
    category: "major",
  },
  {
    name: "Trine",
    symbol: "△",
    angle: 120,
    orb: 8,
    color: "#3498db",
    type: "Soft",
    category: "major",
  },
  {
    name: "Square",
    symbol: "□",
    angle: 90,
    orb: 7,
    color: "#e67e22",
    type: "Hard",
    category: "major",
  },
  {
    name: "Sextile",
    symbol: "⚹",
    angle: 60,
    orb: 6,
    color: "#2ecc71",
    type: "Soft",
    category: "major",
  },
  {
    name: "Semi-Sextile",
    symbol: "⚺",
    angle: 30,
    orb: 1.5,
    color: "#95a5a6",
    type: "Soft",
    category: "minor",
  },
  {
    name: "Quincunx",
    symbol: "⚻",
    angle: 150,
    orb: 1.5,
    color: "#8e44ad",
    type: "Hard",
    category: "minor",
  },
  {
    name: "Quintile",
    symbol: "Q",
    angle: 72,
    orb: 1.5,
    color: "#f1c40f",
    type: "Soft",
    category: "minor",
  },
  {
    name: "Bi-Quintile",
    symbol: "bQ",
    angle: 144,
    orb: 1.5,
    color: "#f39c12",
    type: "Soft",
    category: "minor",
  },
  {
    name: "Septile",
    symbol: "S",
    angle: 51.43,
    orb: 1.5,
    color: "#1abc9c",
    type: "Hard",
    category: "minor",
  },
  {
    name: "Bi-Septile",
    symbol: "S2",
    angle: 102.86,
    orb: 1.5,
    color: "#16a085",
    type: "Hard",
    category: "minor",
  },
  {
    name: "Tri-Septile",
    symbol: "S3",
    angle: 154.29,
    orb: 1.5,
    color: "#0e6655",
    type: "Hard",
    category: "minor",
  },
  {
    name: "Novile",
    symbol: "N",
    angle: 40,
    orb: 1.5,
    color: "#c0392b",
    type: "Soft",
    category: "minor",
  },
  {
    name: "Bi-Novile",
    symbol: "N2",
    angle: 80,
    orb: 1.5,
    color: "#f39c12",
    type: "Soft",
    category: "minor",
  },
  {
    name: "Quad-Novile",
    symbol: "N4",
    angle: 160,
    orb: 1.5,
    color: "#2c3e50",
    type: "Soft",
    category: "minor",
  },
  {
    name: "Semi-Novile",
    symbol: "SN",
    angle: 20,
    orb: 1.0,
    color: "#bdc3c7",
    type: "Soft",
    category: "minor",
  },
  {
    name: "Decile",
    symbol: "⊥",
    angle: 36,
    orb: 1.5,
    color: "#16a085",
    type: "Soft",
    category: "minor",
  },
  {
    name: "Bi-Decile",
    symbol: "D2",
    angle: 72,
    orb: 1.5,
    color: "#27ae60",
    type: "Soft",
    category: "minor",
  },
  {
    name: "Tri-Decile",
    symbol: "D3",
    angle: 108,
    orb: 1.5,
    color: "#2980b9",
    type: "Soft",
    category: "minor",
  },
  {
    name: "Quadra-Decile",
    symbol: "D4",
    angle: 144,
    orb: 1.5,
    color: "#d35400",
    type: "Soft",
    category: "minor",
  },
  {
    name: "Semi-Decile",
    symbol: "SD",
    angle: 18,
    orb: 1.0,
    color: "#7f8c8d",
    type: "Soft",
    category: "minor",
  },
  {
    name: "Semi-Square",
    symbol: "∠",
    angle: 45,
    orb: 1.5,
    color: "#e74c3c",
    type: "Hard",
    category: "minor",
  },
  {
    name: "Sesquiquadrate",
    symbol: "⚼",
    angle: 135,
    orb: 1.5,
    color: "#c0392b",
    type: "Hard",
    category: "minor",
  },
  {
    name: "Semi-Octile",
    symbol: "SO",
    angle: 15,
    orb: 1.0,
    color: "#bdc3c7",
    type: "Hard",
    category: "minor",
  },
  {
    name: "Undecile",
    symbol: "U",
    angle: 165,
    orb: 1.5,
    color: "#34495e",
    type: "Hard",
    category: "minor",
  },
];

const ZODIAC_SIGNS = [
  { symbol: "♈", name: "Aries" },
  { symbol: "♉", name: "Taurus" },
  { symbol: "♊", name: "Gemini" },
  { symbol: "♋", name: "Cancer" },
  { symbol: "♌", name: "Leo" },
  { symbol: "♍", name: "Virgo" },
  { symbol: "♎", name: "Libra" },
  { symbol: "♏", name: "Scorpio" },
  { symbol: "♐", name: "Sagittarius" },
  { symbol: "♑", name: "Capricorn" },
  { symbol: "♒", name: "Aquarius" },
  { symbol: "♓", name: "Pisces" },
];

const SIGN_COLORS_DEFAULT = {
  "♈": "#e74c3c",
  "♉": "#27ae60",
  "♊": "#f1c40f",
  "♋": "#3498db",
  "♌": "#e67e22",
  "♍": "#95a5a6",
  "♎": "#fd79a8",
  "♏": "#8e44ad",
  "♐": "#2980b9",
  "♑": "#2c3e50",
  "♒": "#00cec9",
  "♓": "#74b9ff",
};

export default function HoroscopeChart(props) {
  const {
    transitPositions,
    natalPositions,
    assetName,
    showExtendedPoints,
    chartMode,
    setChartMode,
    aspectCategory,
    setAspectCategory,
    windowWidth,
    birthLabel,
    transitLabel,
    planetColors = {},
    signColors: signColorsMap = SIGN_COLORS_DEFAULT,
    isHeliocentric,
    horoscopeStyle = "style1",
    hideAspectLines = false,
    isDarkMode, // props から正しく抽出
  } = props;

  const svgRef = useRef(null);
  const displaySize = windowWidth < 600 ? 350 : 600;
  const size = 600;
  const center = size / 2;
  const outerRadius = size * 0.44;
  const signRadius = outerRadius - 24;
  const transitRingRadius = signRadius - 45;
  const transitIconBaseRadius = transitRingRadius + 22;
  const natalRingRadius = transitRingRadius - 55;
  const natalIconBaseRadius = natalRingRadius + 20;

  const getCoords = useCallback(
    function (deg, r) {
      const rad = ((deg - 90) * Math.PI) / 180;
      return { x: center + r * Math.cos(rad), y: center + r * Math.sin(rad) };
    },
    [center],
  );

  const getClusteredBodies = useCallback(
    function (positions, baseRadius) {
      if (!positions) return [];
      let activeIds =
        isHeliocentric === true
          ? [
              "Earth",
              "Mercury",
              "Venus",
              "Mars",
              "Jupiter",
              "Saturn",
              "Uranus",
              "Neptune",
              "Pluto",
            ]
          : PLANETS;
      if (showExtendedPoints) activeIds = activeIds.concat(EXTENDED_POINTS);
      const bodies = activeIds
        .map((id) => ({ id: id, lon: positions[id] }))
        .filter((b) => b.lon !== undefined && b.lon !== null)
        .sort((a, b) => a.lon - b.lon);
      if (bodies.length === 0) return [];
      const clusters = [];
      let currentCluster = [];
      bodies.forEach((body) => {
        if (currentCluster.length === 0) currentCluster.push(body);
        else {
          const prev = currentCluster[currentCluster.length - 1];
          if (Math.abs(body.lon - prev.lon) <= 6) currentCluster.push(body);
          else {
            clusters.push(currentCluster);
            currentCluster = [body];
          }
        }
      });
      if (currentCluster.length > 0) clusters.push(currentCluster);
      const results = [];
      clusters.forEach((cluster) => {
        const centerOffset = (cluster.length - 1) / 2;
        cluster.forEach((body, index) => {
          results.push(
            Object.assign({}, body, {
              displayRadius: baseRadius + (index - centerOffset) * 20,
              exactLon: body.lon,
            }),
          );
        });
      });
      return results;
    },
    [showExtendedPoints, isHeliocentric],
  );

  const transitBodies = useMemo(
    () => getClusteredBodies(transitPositions, transitIconBaseRadius),
    [transitPositions, getClusteredBodies, transitIconBaseRadius],
  );
  const natalBodies = useMemo(
    () => getClusteredBodies(natalPositions, natalIconBaseRadius),
    [natalPositions, getClusteredBodies, natalIconBaseRadius],
  );

  const aspectLines = useMemo(() => {
    const results = [];
    if (hideAspectLines === true) return results;
    let calcSet1 = [],
      calcSet2 = [],
      r1 = 0,
      r2 = 0;
    if (chartMode === "transit-natal") {
      calcSet1 = transitBodies;
      r1 = transitRingRadius;
      calcSet2 = natalBodies;
      r2 = natalRingRadius;
    } else if (chartMode === "transit") {
      calcSet1 = transitBodies;
      r1 = transitRingRadius;
      calcSet2 = transitBodies;
      r2 = transitRingRadius;
    } else {
      calcSet1 = natalBodies;
      r1 = natalRingRadius;
      calcSet2 = natalBodies;
      r2 = natalRingRadius;
    }
    if (!calcSet1 || !calcSet2) return results;
    calcSet1.forEach((b1, i) => {
      calcSet2.forEach((b2, j) => {
        if (chartMode !== "transit-natal" && j <= i) return;
        // 特定の天体・感受点（SouthNode, ASC, DSC, MC, IC）はアスペクトを表示しない
        const skipIds = ["SouthNode", "ASC", "DSC", "MC", "IC"];
        if (skipIds.includes(b1.id) || skipIds.includes(b2.id)) return;

        if (
          b1.id === b2.id &&
          b1.exactLon === b2.exactLon &&
          chartMode !== "transit-natal"
        )
          return;
        let diff = Math.abs(b1.exactLon - b2.exactLon);
        if (diff > 180) diff = 360 - diff;
        ASPECT_DEFS.forEach((asp) => {
          const isMaj = asp.category === "major";
          if (aspectCategory === "major" && !isMaj) return;
          if (aspectCategory === "minor" && isMaj) return;
          if (aspectCategory === "soft" && asp.type !== "Soft") return;
          if (aspectCategory === "hard" && asp.type !== "Hard") return;

          // ノードの場合はオーブを狭く設定（最大4度程度に制限）
          const isNodeAspect = b1.id === "NorthNode" || b2.id === "NorthNode";
          const currentOrb = isNodeAspect ? Math.min(asp.orb, 4.0) : asp.orb;

          if (Math.abs(diff - asp.angle) <= currentOrb) {
            const p1 = getCoords(b1.exactLon, r1);
            const p2 = getCoords(b2.exactLon, r2);
            results.push({
              x1: p1.x,
              y1: p1.y,
              x2: p2.x,
              y2: p2.y,
              color: asp.color,
              orb: Math.abs(diff - asp.angle),
              symbol: asp.symbol,
              isNode: isNodeAspect,
            });
          }
        });
      });
    });
    return results;
  }, [
    chartMode,
    transitBodies,
    natalBodies,
    aspectCategory,
    transitRingRadius,
    natalRingRadius,
    getCoords,
    hideAspectLines,
  ]);

  const saveAsImage = () => {
    if (!svgRef.current) return;
    const svgData = new XMLSerializer().serializeToString(svgRef.current);
    const canvas = document.createElement("canvas");
    canvas.width = 1200;
    canvas.height = 1200;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = isDarkMode ? "#1a191e" : "white";
      ctx.fillRect(0, 0, 1200, 1200);
      ctx.drawImage(img, 0, 0, 1200, 1200);

      const getFormatDate = (label) => {
        if (!label) return "";
        const match = label.match(/(\d{4})\/(\d{2})\/(\d{2})/);
        return match ? `${match[1]}-${match[2]}-${match[3]}` : "";
      };
      
      const bDate = getFormatDate(birthLabel);
      const tDate = getFormatDate(transitLabel);
      let filename = "Horoscope";
      if (bDate) filename += `_${bDate}`;
      if (tDate) filename += `_${tDate}`;
      if (filename === "Horoscope") filename += `_${assetName || "chart"}`;
      filename += ".png";

      const link = document.createElement("a");
      link.download = filename;
      link.href = canvas.toDataURL("image/png");
      link.click();
    };
    img.src =
      "data:image/svg+xml;base64," +
      btoa(unescape(encodeURIComponent(svgData)));
  };

  /* --- HoroscopeChart.jsx 295行目付近からの修正 --- */

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "10px",
          marginBottom: "20px",
          flexWrap: "wrap",
        }}
      >
        <select
          className="pill-select"
          value={chartMode}
          onChange={function (e) {
            setChartMode(e.target.value);
          }}
          style={{
            padding: "8px 16px",
            borderRadius: "20px",
            border: isDarkMode
              ? "1px solid rgba(255,255,255,0.1)"
              : "1px solid #ddd",
            fontSize: "12px",
            backgroundColor: isDarkMode ? "#2d2c33" : "#fff",
            color: isDarkMode ? "#fff" : "#333",
            fontWeight: "bold",
            outline: "none",
            colorScheme: isDarkMode ? "dark" : "light",
          }}
        >
          <option
            value="transit-natal"
            style={{
              backgroundColor: isDarkMode ? "#2d2c33" : "#fff",
              color: isDarkMode ? "#fff" : "#333",
            }}
          >
            二重円 (Transit × Natal)
          </option>
          <option
            value="transit"
            style={{
              backgroundColor: isDarkMode ? "#2d2c33" : "#fff",
              color: isDarkMode ? "#fff" : "#333",
            }}
          >
            トランジット
          </option>
          <option
            value="natal"
            style={{
              backgroundColor: isDarkMode ? "#2d2c33" : "#fff",
              color: isDarkMode ? "#fff" : "#333",
            }}
          >
            ネイタル
          </option>
        </select>
        <select
          className="pill-select"
          value={aspectCategory}
          onChange={function (e) {
            setAspectCategory(e.target.value);
          }}
          style={{
            padding: "8px 16px",
            borderRadius: "20px",
            border: isDarkMode
              ? "1px solid rgba(255,255,255,0.1)"
              : "1px solid #ddd",
            fontSize: "12px",
            backgroundColor: isDarkMode ? "#2d2c33" : "#fff",
            color: isDarkMode ? "#fff" : "#333",
            fontWeight: "bold",
            outline: "none",
            colorScheme: isDarkMode ? "dark" : "light",
          }}
        >
          <option
            value="all"
            style={{
              backgroundColor: isDarkMode ? "#2d2c33" : "#fff",
              color: isDarkMode ? "#fff" : "#333",
            }}
          >
            すべて表示
          </option>
          <option
            value="major"
            style={{
              backgroundColor: isDarkMode ? "#2d2c33" : "#fff",
              color: isDarkMode ? "#fff" : "#333",
            }}
          >
            主要アスペクト
          </option>
          <option
            value="minor"
            style={{
              backgroundColor: isDarkMode ? "#2d2c33" : "#fff",
              color: isDarkMode ? "#fff" : "#333",
            }}
          >
            マイナーアスペクト
          </option>
          <option
            value="soft"
            style={{
              backgroundColor: isDarkMode ? "#2d2c33" : "#fff",
              color: isDarkMode ? "#fff" : "#333",
            }}
          >
            ソフトアスペクト
          </option>
          <option
            value="hard"
            style={{
              backgroundColor: isDarkMode ? "#2d2c33" : "#fff",
              color: isDarkMode ? "#fff" : "#333",
            }}
          >
            ハードアスペクト
          </option>
        </select>
      </div>

      <div
        style={{
          background: isDarkMode ? "transparent" : "#ffffff",
          borderRadius: "50%",
          padding: "10px",
          boxShadow: isDarkMode ? "none" : "0 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        <svg
          ref={svgRef}
          width={displaySize}
          height={displaySize}
          viewBox={"0 0 " + size + " " + size}
          style={{ maxWidth: "100%", height: "auto" }}
        >
          <g style={{ userSelect: "none" }}>
            {birthLabel && (
              <text
                x="0"
                y="12"
                fontSize="11"
                fill={isDarkMode ? "#ffffff" : "#5f5f5f"}
                fontWeight="normal"
                textAnchor="start"
              >
                <tspan x="0" dy="0" fontWeight="bold">Inner Wheel</tspan>
                {assetName && <tspan x="0" dy="14" fontWeight="bold">{assetName}</tspan>}
                <tspan x="0" dy="14">Natal Chart</tspan>
                {birthLabel.split(' UTC').length > 1 ? (
                  <>
                    <tspan x="0" dy="14">{birthLabel.split(' UTC')[0]}</tspan>
                    <tspan x="0" dy="14">UTC{birthLabel.split(' UTC')[1]}</tspan>
                  </>
                ) : (
                  <tspan x="0" dy="14">{birthLabel}</tspan>
                )}
              </text>
            )}
            {transitLabel && (
              <text
                x={size}
                y="12"
                fontSize="11"
                fill={isDarkMode ? "#ffffff" : "#5f5f5f"}
                fontWeight="normal"
                textAnchor="end"
              >
                <tspan x={size} dy="0" fontWeight="bold">Outer Wheel</tspan>
                <tspan x={size} dy="14">Transit Chart</tspan>
                {transitLabel.split(' UTC').length > 1 ? (
                  <>
                    <tspan x={size} dy="14">{transitLabel.split(' UTC')[0]}</tspan>
                    <tspan x={size} dy="14">UTC{transitLabel.split(' UTC')[1]}</tspan>
                  </>
                ) : (
                  <tspan x={size} dy="14">{transitLabel}</tspan>
                )}
              </text>
            )}
          </g>
          <circle
            cx={center}
            cy={center}
            r={outerRadius}
            fill="none"
            stroke={isDarkMode ? "rgba(255,255,255,0.3)" : "#6b6b6b"}
            strokeWidth="1"
          />
          {Array.from({ length: 360 }).map((_, deg) => {
            const isMajorTick = deg % 30 === 0;
            const isDecanTick = deg % 10 === 0 && !isMajorTick;
            const isMediumTick = deg % 5 === 0 && !isMajorTick && !isDecanTick;
            const tickInnerR = isMajorTick
              ? outerRadius - 14
              : isDecanTick
                ? outerRadius - 10
                : isMediumTick
                  ? outerRadius - 7
                  : outerRadius - 4;
            const p1 = getCoords(deg, outerRadius);
            const p2 = getCoords(deg, tickInnerR);
            const labelPos = getCoords(deg, outerRadius + 14);
            return (
              <g key={"tick-" + deg}>
                <line
                  x1={p1.x}
                  y1={p1.y}
                  x2={p2.x}
                  y2={p2.y}
                  stroke={
                    isDarkMode
                      ? isMajorTick
                        ? "#ffffff"
                        : isDecanTick
                          ? "rgba(255,255,255,0.6)"
                          : "rgba(255,255,255,0.3)"
                      : isMajorTick
                        ? "#1e293b"
                        : isDecanTick
                          ? "#64748b"
                          : "#cbd5e1"
                  }
                  strokeWidth={isMajorTick ? 2 : 1}
                />
                {isMajorTick && (
                  <text
                    x={labelPos.x}
                    y={labelPos.y}
                    fontSize="10"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={isDarkMode ? "#ffffff" : "#475569"}
                    fontWeight="900"
                  >
                    {deg}°
                  </text>
                )}
              </g>
            );
          })}
          {Array.from({ length: 12 }).map((_, i) => {
            const p1 = getCoords(i * 30, outerRadius);
            const p2 = getCoords(i * 30, natalRingRadius - 30);
            return (
              <line
                key={i}
                x1={p1.x}
                y1={p1.y}
                x2={p2.x}
                y2={p2.y}
                stroke={isDarkMode ? "rgba(255,255,255,0.1)" : "#f1f5f9"}
                strokeWidth="1.5"
              />
            );
          })}
          {ZODIAC_SIGNS.map((sign, i) => {
            const pos = getCoords(i * 30 + 15, outerRadius + 35);
            const signColor =
              horoscopeStyle === "style2"
                ? isDarkMode
                  ? "#ffffff"
                  : "#000000"
                : signColorsMap[sign.symbol] ||
                  SIGN_COLORS_DEFAULT[sign.symbol];
            return (
              <text
                key={sign.name}
                x={pos.x}
                y={pos.y}
                fontSize="14"
                textAnchor="middle"
                dominantBaseline="middle"
                fill={signColor}
                style={{ userSelect: "none" }}
              >
                {sign.symbol + "\uFE0E"}
              </text>
            );
          })}
          <circle
            cx={center}
            cy={center}
            r={transitRingRadius}
            fill="none"
            stroke="#2c2d96"
            strokeWidth="1.2"
            opacity="0.4"
          />
          <circle
            cx={center}
            cy={center}
            r={natalRingRadius}
            fill="none"
            stroke="#9c2393"
            strokeWidth="1.2"
            strokeDasharray="4,4"
            opacity="0.4"
          />
          {aspectLines.map((line, i) => (
            <g key={"aspect-group-" + i}>
              <line
                x1={line.x1}
                y1={line.y1}
                x2={line.x2}
                y2={line.y2}
                stroke={line.color}
                strokeWidth={line.isNode ? 0.4 : (line.orb <= 0.5 ? 2 : 1)}
                strokeOpacity={line.isNode ? 0.3 : (line.orb <= 0.5 ? 0.8 : 0.4)}
                strokeDasharray={line.isNode ? "3,3" : "none"}
              />
              {line.symbol && (
                <g
                  transform={
                    "translate(" +
                    (line.x1 + line.x2) / 2 +
                    ", " +
                    (line.y1 + line.y2) / 2 +
                    ")"
                  }
                >
                  <circle
                    r="8"
                    fill={isDarkMode ? "#1a191e" : "white"}
                    stroke={line.color}
                    strokeWidth="0.5"
                    opacity="0.9"
                  />
                  <text
                    y="1"
                    fontSize="11"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={line.color}
                    fontWeight="bold"
                    style={{ userSelect: "none", pointerEvents: "none" }}
                  >
                    {line.symbol}
                  </text>
                </g>
              )}
            </g>
          ))}
          {(chartMode === "natal" || chartMode === "transit-natal") &&
            natalBodies
              .filter((b) => !["ASC", "DSC", "MC", "IC"].includes(b.id))
              .map((body) => {
              const pos = getCoords(body.exactLon, body.displayRadius);
              const pColor =
                horoscopeStyle === "style2"
                  ? isDarkMode
                    ? "#ffffff"
                    : "#000000"
                  : planetColors[body.id] || "#9c2393";
              return (
                <g key={"n-" + body.id}>
                  {horoscopeStyle === "style1" && (
                    <circle
                      cx={pos.x}
                      cy={pos.y}
                      r="10"
                      fill={isDarkMode ? "#1a191e" : "white"}
                      stroke={pColor}
                      strokeWidth="1.5"
                    />
                  )}
                  <text
                    x={pos.x}
                    y={pos.y + (horoscopeStyle === "style1" ? 4 : 5)}
                    fontSize="12"
                    textAnchor="middle"
                    fill={pColor}
                    fontWeight="bold"
                    style={{ userSelect: "none" }}
                  >
                    {PLANET_SYMBOLS[body.id] || body.id.charAt(0)}
                  </text>
                </g>
              );
            })}
          {(chartMode === "transit" || chartMode === "transit-natal") &&
            transitBodies
              .filter((b) => !["ASC", "DSC", "MC", "IC"].includes(b.id))
              .map((body) => {
              const pos = getCoords(body.exactLon, body.displayRadius);
              const pColor =
                horoscopeStyle === "style2"
                  ? isDarkMode
                    ? "#ffffff"
                    : "#000000"
                  : planetColors[body.id] || "#2c2d96";
              return (
                <g key={"t-" + body.id}>
                  {horoscopeStyle === "style1" && (
                    <circle
                      cx={pos.x}
                      cy={pos.y}
                      r="12"
                      fill={isDarkMode ? "#1a191e" : "white"}
                      stroke={pColor}
                      strokeWidth="2"
                    />
                  )}
                  <text
                    x={pos.x}
                    y={pos.y + (horoscopeStyle === "style1" ? 5 : 6)}
                    fontSize="14"
                    textAnchor="middle"
                    fill={pColor}
                    fontWeight="bold"
                    style={{ userSelect: "none" }}
                  >
                    {PLANET_SYMBOLS[body.id] || body.id.charAt(0)}
                  </text>
                </g>
              );
            })}

          {/* 感受点（ASC, DSC, MC, IC）の太ライン描画 */}
          {["ASC", "DSC", "MC", "IC"].map((id) => {
            let lon = null;
            if (chartMode === "natal" || chartMode === "transit-natal") {
              lon = natalPositions ? natalPositions[id] : null;
            } else if (chartMode === "transit") {
              lon = transitPositions ? transitPositions[id] : null;
            }
            if (lon === undefined || lon === null) return null;

            const pLineStart = getCoords(lon, natalRingRadius - 30);
            const pLineEnd = getCoords(lon, outerRadius + 15);
            // ラベル位置を内側に移動して星座アイコンとの重なりを防ぐ
            const pLabel = getCoords(lon, outerRadius - 45);
            const color = planetColors[id] || (isDarkMode ? "#ffffff" : "#1e293b");

            return (
              <g key={"angle-line-" + id}>
                <line
                  x1={pLineStart.x}
                  y1={pLineStart.y}
                  x2={pLineEnd.x}
                  y2={pLineEnd.y}
                  stroke={color}
                  strokeWidth="0.5"
                  strokeLinecap="round"
                />
                <text
                  x={pLabel.x}
                  y={pLabel.y}
                  fontSize="12"
                  fontWeight="bold"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill={color}
                  style={{ 
                    userSelect: "none",
                    filter: isDarkMode ? "drop-shadow(0px 0px 2px rgba(0,0,0,0.8))" : "none"
                  }}
                >
                  {id}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      <div
        style={{
          marginTop: "15px",
          display: "flex",
          gap: "20px",
          fontSize: "11px",
          color: isDarkMode ? "#ffffff" : "#555",
          fontWeight: "500",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              background: "#2c2d96",
            }}
          ></div>
          <span>Transit (Blue Solid)</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              border: "1px dashed #9c2393",
            }}
          ></div>
          <span>Natal (Purple Dashed)</span>
        </div>
      </div>

      <button
        onClick={saveAsImage}
        style={{
          marginTop: "20px",
          background: "none",
          border: "none",
          color: isDarkMode ? "#ffffff" : "#2c2d96",
          display: "flex",
          alignItems: "center",
          gap: "6px",
          fontSize: "14px",
          fontWeight: "bold",
          cursor: "pointer",
        }}
      >
        <Download size={18} /> Download Chart
      </button>
    </div>
  );
}
